import React, { useState } from 'react';
import { Upload, Button, Modal, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import api from '../api/api';

interface ImageUploaderProps {
  initialImage?: string;
  onImageChange: (file: File | null) => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ initialImage, onImageChange }) => {
  const [previewImage, setPreviewImage] = useState<string | null>(initialImage || null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>(
    initialImage ? [{ uid: '-1', name: 'image.png', status: 'done', url: initialImage }] : []
  );
  const [uploading, setUploading] = useState(false);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleCancel = () => setPreviewOpen(false);

  const uploadButton = (
    <div>
      <div style={{ marginTop: 8 }}>
        <Button icon={<UploadOutlined />} loading={uploading}>
          Upload
        </Button>
      </div>
    </div>
  );

  const customUpload: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('profile_picture', file as RcFile);

      const response = await api.post('v1/profile/picture/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update the file status and URL
      const updatedFileList = fileList.map(item => {
        if (item.uid === (file as UploadFile).uid) {
          return {
            ...item,
            status: 'done' as const,
            url: response.data.url, // Assuming the API returns the image URL
          };
        }
        return item;
      });
      setFileList(updatedFileList);

      // Call the parent component's onImageChange
      onImageChange(file as File);

      message.success('Upload successful!');
      onSuccess?.(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Upload failed. Please try again.');
      
      // Update the file status to error
      const updatedFileList = fileList.map(item => {
        if (item.uid === (file as UploadFile).uid) {
          return {
            ...item,
            status: 'error' as const,
          };
        }
        return item;
      });
      setFileList(updatedFileList);
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative flex justify-center">
        <Upload
          listType="picture-circle"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          maxCount={1}
          customRequest={customUpload}
          accept="image/*"
          beforeUpload={(file) => {
            // Validate file type
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
              message.error('You can only upload image files!');
              return false;
            }

            // Validate file size (e.g., 5MB limit)
            const isLt5M = file.size / 1024 / 1024 < 2;
            if (!isLt5M) {
              message.error('Image must be smaller than 2MB!');
              return false;
            }

            return true;
          }}
        >
          {fileList.length >= 1 ? null : uploadButton}
        </Upload>
      </div>

      <Modal
        open={previewOpen}
        title="Image Preview"
        footer={null}
        onCancel={handleCancel}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage || ''} />
      </Modal>
    </div>
  );
};

// Helper function to convert file to base64
const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export default ImageUploader; 